import { toast } from 'react-toastify'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { useNavigate } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import SearchPage from 'pages/app/mobile/search'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { FormProvider, useForm } from 'react-hook-form'
import ContactDetailsStep from 'components/Modals/ContactModal/ContactDetailsStep'
import {
  ContactDefaultValues,
  getNewContactFormShape,
} from 'components/Modals/ContactModal/ContactModal'
import { zodResolver } from '@hookform/resolvers/zod'
import apiService from 'services/api'
import to from 'await-to-js'
import { handleFormError } from 'utils/handleFormError'

export default function MobileContactsCreate() {
  const navigate = useNavigate()
  const api = apiService()
  const [selectedSearchResult, setSelectedSearchResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()

  const isDoorSelected =
    selectedSearchResult && 'chain_proxy_id' in selectedSearchResult

  // For company selection: fetch chain data using item_id (chain ID)
  const chainId =
    selectedSearchResult && !isDoorSelected
      ? Number(selectedSearchResult.item_id)
      : undefined

  const { data: chainData } = api.useGetChain(chainId)

  // For door selection: fetch door proxy data using item_id (restaurant ID)
  const restaurantId =
    selectedSearchResult && isDoorSelected
      ? String(selectedSearchResult.item_id)
      : undefined

  const { data: doorProxy } = api.useGetPlaceV2(restaurantId)

  // Construct forCompany from the fetched data
  const forCompany = useMemo(() => {
    if (!selectedSearchResult) return undefined

    if (isDoorSelected && doorProxy) {
      // For door selection, use the chain_proxy from door proxy data
      return {
        id: doorProxy.chain_proxy.id,
        name: doorProxy.chain_proxy.name,
        domain: doorProxy.chain_proxy.domain,
        category: doorProxy.chain_proxy.category,
        sub_category: doorProxy.chain_proxy.sub_category,
      }
    } else if (!isDoorSelected && chainData) {
      // For company selection, use the chain data
      // Note: chainData doesn't have category/sub_category directly
      // The ContactDetailsStep will handle company selection without these fields
      return {
        id: chainData.contact_company_id,
        name: chainData.chain,
        domain: chainData.domain,
      }
    }

    return undefined
  }, [selectedSearchResult, isDoorSelected, chainData, doorProxy])

  const newContactFormShape = useMemo(() => {
    return getNewContactFormShape(forCompany)
  }, [forCompany])

  const handleClose = () => {
    navigate('/contacts')
  }

  const handleSubmitSuccess = () => {
    toast.success('Contact created successfully')
    handleClose()
  }

  const submitApiAction = async (values: ContactDefaultValues) => {
    // Transform only the email fields from empty strings to null
    const transformedEmails = {
      email_work: values.email_work === '' ? null : values.email_work,
      email_personal:
        values.email_personal === '' ? null : values.email_personal,
      email_generic: values.email_generic === '' ? null : values.email_generic,
    }

    // Merge transformed emails with the rest of the form values
    const transformedValues: ContactDefaultValues = {
      ...values,
      ...transformedEmails,
    }

    return api.createContact({
      ...transformedValues,
      contact_company: {
        ...transformedValues.contact_company,
        domain: transformedValues.company_domain, // Move company_domain to company object so we override it on the backend if needed
      },
    })
  }

  const defaultValues = useCallback(() => {
    return {
      first_name: '',
      last_name: '',
      consent_to_contact: false,
      url: '',
      linkedin_url: '',
      notes: '',
      title: '',
      seniority: undefined,
      department: undefined,
      tags: [],
      contact_company: {} as ContactDefaultValues['contact_company'],
      company_domain: '',
      email_work: '',
      email_personal: '',
      email_generic: '',
      phone_number: '',
      extension: '',
      apply_to: 'entire_company',
      door_ids: [],
    } as ContactDefaultValues
  }, [])

  const formMethods = useForm({
    defaultValues: defaultValues(),
    mode: 'onChange',
    resolver: zodResolver(newContactFormShape),
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldUnregister: false,
  })

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
  } = formMethods

  // Reset form when company data is loaded
  useEffect(() => {
    if (forCompany) {
      reset({
        ...defaultValues(),
        contact_company: forCompany as any,
        company_domain: forCompany.domain ?? '',
      })
    }
  }, [forCompany, reset, defaultValues])

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(submitApiAction(values))
      if (err) {
        console.warn(err)
        handleFormError(err, setError)
        return
      }

      handleSubmitSuccess()
    },
    (err) => console.warn(err)
  )

  return (
    <MobileModal
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Create a contact
          </div>
          {selectedSearchResult ? (
            <h1 className="text-2xl font-semibold text-gray-900">
              Choose a company
            </h1>
          ) : (
            <h1 className="text-2xl font-semibold text-gray-900">
              Add a new contact
            </h1>
          )}

          <div className="text-xs text-gray-500 mt-2">
            Contacts match to companies based on their domain.
          </div>
        </div>
      }
      footer={
        selectedSearchResult ? (
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={() => {
                  setSelectedSearchResult(undefined)
                  reset(defaultValues())
                }}
              >
                Cancel
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={onSubmit}
                disabled={!isValid || isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Contact'}
              </FbButton>
            </div>
          </div>
        ) : (
          <div></div>
        )
      }
    >
      {!selectedSearchResult ? (
        <SearchPage
          hideBack
          onClick={(company) => {
            setSelectedSearchResult(company)
          }}
        />
      ) : (
        <FormProvider {...formMethods}>
          <ContactDetailsStep forCompany={forCompany} />
        </FormProvider>
      )}
    </MobileModal>
  )
}
